// src/components/common/Hero.js
// Filepath: // src/components/common/Hero.js
import React from "react";
import { Box, Typography, Chip, Stack } from "@mui/material";

/**
 * Props:
 *  - title: string
 *  - subtitle?: string
 *  - backgroundUrl?: string
 *  - gradientStops?: string
 *  - textTop?: string | number   (e.g., "14vh")
 *  - textLeft?: string | number  (e.g., 128)
 *  - byline?: string             (e.g., "Jane Doe • 2024")
 *  - meta?: string               (e.g., "Oil on canvas")
 *  - ownershipLabel?: string     (e.g., "You own 3 shares ($42)")
 *
 * Changes:
 *  - Title: single-line, no wrap, ellipsis
 *  - Description (subtitle): two-line clamp with ellipsis
 *  - Optional: byline, meta, chips row (badges + ownership)
 *  - TV tweak: larger byline/meta/chips for 10-foot readability
 */
export default function Hero({
  title,
  subtitle,
  backgroundUrl,
  gradientStops,
  textTop = "14vh",
  textLeft = 128,
  byline,
  meta,
  badges = [],
  ownershipLabel,
}) {
  const gradient =
    gradientStops ||
    "linear-gradient(135deg, rgba(0,0,0,0.92) 10%, rgba(0,0,0,0.72) 38%, rgba(0,0,0,0.42) 68%, rgba(0,0,0,0.18) 100%)";

  const hasChips = (Array.isArray(badges) && badges.length > 0) || Boolean(ownershipLabel);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        borderRadius: 0,
        overflow: "hidden",
        background: backgroundUrl
          ? `url(${backgroundUrl}) top center / cover no-repeat`
          : "linear-gradient(135deg, #1e2430, #10131a)",
        backgroundColor: "rgba(26,10,40,0.55)",
        backgroundBlendMode: backgroundUrl ? "multiply" : "normal",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: gradient,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: textTop,
          left: textLeft,
          right: 32,
          maxWidth: { xs: "92%", md: "76%", lg: "78%" },
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="h1"
          noWrap
          sx={{
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: 0.2,
            fontSize: { xs: "2.8rem", md: "4.0rem", lg: "4.6rem" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={typeof title === "string" ? title : undefined}
        >
          {title}
        </Typography>

        {byline && (
          <Typography
            sx={{
              mt: 0.6,
              color: "rgba(255,255,255,0.92)",
              // Bigger for TV
              fontSize: { xs: "1.20rem", md: "1.34rem", lg: "1.48rem" },
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: 0.15,
            }}
            title={byline}
          >
            {byline}
          </Typography>
        )}

        {meta && (
          <Typography
            sx={{
              mt: 0.35,
              color: "rgba(255,255,255,0.80)",
              // Bigger for TV
              fontSize: { xs: "1.10rem", md: "1.22rem", lg: "1.30rem" },
              lineHeight: 1.25,
              letterSpacing: 0.12,
            }}
            title={meta}
          >
            {meta}
          </Typography>
        )}

        {subtitle && (
          <Typography
            sx={{
              mt: 1.1,
              color: "rgba(255,255,255,0.86)",
              fontSize: { xs: "1.10rem", md: "1.22rem" },
              maxWidth: 1000,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={typeof subtitle === "string" ? subtitle : undefined}
          >
            {subtitle}
          </Typography>
        )}

        {hasChips && (
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ mt: 1.35, flexWrap: "wrap", rowGap: 1.1 }}
          >
            {badges.map((b, i) => (
              <Chip
                key={`${b}-${i}`}
                label={b}
                size="medium" // was "small"
                sx={{
                  pointerEvents: "none",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.95)",
                  borderColor: "rgba(255,255,255,0.24)",
                  // Larger label for TV
                  "& .MuiChip-label": {
                    px: { xs: 1.25, md: 1.5 },
                    py: 0.25,
                    fontSize: { xs: "0.98rem", md: "1.04rem", lg: "1.06rem" },
                    fontWeight: 600,
                    letterSpacing: 0.12,
                  },
                }}
                variant="outlined"
              />
            ))}
            {ownershipLabel && (
              <Chip
                label={ownershipLabel}
                size="medium" // was "small"
                sx={{
                  pointerEvents: "none",
                  bgcolor: "rgba(46, 204, 113, 0.22)",
                  color: "rgba(255,255,255,0.95)",
                  borderColor: "rgba(46, 204, 113, 0.5)",
                  "& .MuiChip-label": {
                    px: { xs: 1.25, md: 1.5 },
                    py: 0.25,
                    fontSize: { xs: "0.98rem", md: "1.04rem", lg: "1.06rem" },
                    fontWeight: 700,
                    letterSpacing: 0.12,
                  },
                }}
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
